import React, {useState} from 'react';
import PropTypes from 'prop-types';
import styles from './NumberPicker.module.scss';
import {refSelectAll} from "../../../tools/tools";

const NumberPicker = ({
          value, min, max, disabled, changeHandler, editable, inputRef, children, focusHandler, blurHandler,
          outOfBoundsHandler, containerClasses, outOfBoundsAdjustValue, ...props}) => {

    const [isEmpty, setEmpty] = useState(false);

    if (!inputRef && editable) {
        inputRef = React.createRef();
    }

    const inBoundaries = n => (isNaN(min) && isNaN(max) && !isNaN(n))
        || (!isNaN(n) && !isNaN(min) && !isNaN(max) && n >= min && n <= max)
        || (!isNaN(n) && !isNaN(min) && isNaN(max) && n >= min)
        || (!isNaN(n) && !isNaN(max) && isNaN(min) && n <= max);

    const pressHandler = n => {
        const nextValue = value + n;
        if (inBoundaries(nextValue)) {
            return changeHandler(nextValue);
        }

        return outOfBoundsHandler(nextValue);
    };

    const decBtn = () => {
        const isDisabled = disabled || (!isNaN(min) && value <= min);
        const clickHandler = isDisabled ? () => outOfBoundsHandler(value) : () => pressHandler(-1);
        const btnClasses = [styles.generalBtn];
        const iconClasses = ['fa fa-minus', styles.generalBtnIcon];

        if (isDisabled) btnClasses.push(styles.generalBtnDisabled);

        return (
            <div onClick={clickHandler}
                 className={btnClasses.join(' ')}>
                <i className={iconClasses.join(' ')}/>
            </div>
        );
    };

    const incBtn = () => {
        const isDisabled = disabled || (!isNaN(max) && value >= max);
        const clickHandler = isDisabled ? () => outOfBoundsHandler(value) : () => pressHandler(1);
        const btnClasses = [styles.generalBtn];
        const iconClasses = ['fa fa-plus', styles.generalBtnIcon];

        if (isDisabled) btnClasses.push(styles.generalBtnDisabled);

        return (
            <div onClick={clickHandler}
                 className={btnClasses.join(' ')}>
                <i className={iconClasses.join(' ')}/>
            </div>
        );
    };

    const renderChildren = () => children && (
        <div className={styles.childrenContainer}>
            {children}
        </div>
    );

    const changeInputHandler = e => {
        const {target} = e;
        let {value} = target;
        if (value.includes('+')) return pressHandler(1);
        if (value.includes('-')) return pressHandler(-1);

        if (value === '') {
            return setEmpty(true);
        }

        value = parseInt(value);
        if (outOfBoundsAdjustValue && value < min) value = min;
        if (outOfBoundsAdjustValue && value > max) value = max;
        if (inBoundaries(value)) {
            changeHandler(value);
            setEmpty(false);
            return;
        }

        outOfBoundsHandler(value);
    };

    const localFocusHandler = e => {
        refSelectAll(inputRef);
        focusHandler(e);
    };

    const localBlurHandler = e => {
        setEmpty(false);
        blurHandler(e);
    };

    const renderValue = () => {
        if (!editable) {
            return value;
        }

        return (
            <input value={isEmpty ? '' : value}
                   ref={inputRef}
                   tabIndex={0}
                   onBlur={localBlurHandler}
                   onFocus={localFocusHandler}
                   onChange={changeInputHandler}/>
        );
    };

    return (
        <div className={[styles.container, containerClasses].join(' ')}>
            {renderChildren()}
            {decBtn()}
            <div className={styles.valueContainer}>{renderValue()}</div>
            {incBtn()}
        </div>
    );
};

NumberPicker.propTypes = {
    value: PropTypes.any,
    min: PropTypes.number,
    max: PropTypes.number,
    editable: PropTypes.bool,
    disabled: PropTypes.bool,
    children: PropTypes.any,
    inputRef: PropTypes.any,
    containerClasses: PropTypes.string,
    outOfBoundsAdjustValue: PropTypes.bool,
    outOfBoundsHandler: PropTypes.func,
    focusHandler: PropTypes.func,
    blurHandler: PropTypes.func,
    changeHandler: PropTypes.func.isRequired,
};

NumberPicker.defaultProps = {
    value: null,
    min: null,
    max: null,
    editable: false,
    disabled: false,
    children: null,
    inputRef: null,
    containerClasses: '',
    outOfBoundsAdjustValue: false,
    outOfBoundsHandler: () => null,
    focusHandler: () => null,
    blurHandler: () => null,
    changeHandler: () => null,
};

export default NumberPicker;
